import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import * as signalR from '@microsoft/signalr';
import { useAuth } from '@hooks/useAuth';
import { useToast } from '@/context/ToastContext';
import { API_BASE_URL } from '@/shared/api/apiClient';
import { getToken } from '@/services/authService';
import {
  NotificationDto,
  NotificationListDto,
  notificationService,
} from '@/services/notificationService';

// Real time delivers notifications instantly; this poll is only a low-frequency safety net
// for the rare case a socket drops between events (reconnect also refetches).
const SAFETY_POLL_MS = 5 * 60_000; // 5 minutes

const PAGE_SIZE = 20;

interface NotificationContextValue {
  notifications: NotificationDto[];
  totalCount: number;
  unreadCount: number;
  loading: boolean;
  /** True while an additional page is being appended (not the initial load). */
  loadingMore: boolean;
  /** True when older notifications remain on the server. */
  hasMore: boolean;
  error: string | null;
  /** True while the realtime socket is connected. */
  realtimeConnected: boolean;
  refresh: () => void;
  loadMore: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

/** Derives the SignalR hub origin from the API base URL (which ends in `/api` or `/api/v1`). */
function getHubUrl(): string {
  return `${API_BASE_URL.replace(/\/api(\/v\d+)?\/?$/, '')}/hubs/notifications`;
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const toast = useToast();

  const [data, setData] = useState<NotificationListDto | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [realtimeConnected, setRealtimeConnected] = useState(false);

  // Keep a stable ref to the toast API (its object identity changes each render).
  const toastRef = useRef(toast);
  toastRef.current = toast;

  // Snapshots for rolling back optimistic read-state updates when the API rejects.
  const dataRef = useRef(data);
  dataRef.current = data;
  const unreadRef = useRef(unreadCount);
  unreadRef.current = unreadCount;

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [list, unread] = await Promise.all([
        notificationService.getNotifications(1, PAGE_SIZE),
        notificationService.getUnreadCount(),
      ]);
      setData(list);
      setPage(1);
      setUnreadCount(unread.count);
    } catch {
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingMore) return;
    const next = page + 1;
    try {
      setLoadingMore(true);
      const list = await notificationService.getNotifications(next, PAGE_SIZE);
      setData((prev) => {
        if (!prev) return list;
        // Realtime prepends shift the server-side window, so a page can repeat rows.
        const seen = new Set(prev.items.map((i) => i.id));
        return {
          ...prev,
          items: [...prev.items, ...list.items.filter((i) => !seen.has(i.id))],
          totalCount: list.totalCount,
        };
      });
      setPage(next);
    } catch {
      toastRef.current.error('Failed to load more notifications');
    } finally {
      setLoadingMore(false);
    }
  }, [page, loadingMore]);

  // Realtime connection + safety poll — active only while authenticated.
  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      setData(null);
      setUnreadCount(0);
      setPage(1);
      return;
    }

    let disposed = false;
    fetchAll();
    const interval = setInterval(fetchAll, SAFETY_POLL_MS);

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(getHubUrl(), {
        accessTokenFactory: () => getToken() ?? '',
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.on('notificationReceived', (payload: { notification?: NotificationDto; unreadCount?: number }) => {
      const n = payload?.notification;
      if (!n) return;

      setData((prev) => {
        // Ignore if a poll already fetched it.
        if (prev?.items.some((i) => i.id === n.id)) return prev;
        const items = [n, ...(prev?.items ?? [])];
        return prev
          ? { ...prev, items, totalCount: prev.totalCount + 1 }
          : { items, totalCount: 1, page: 1, pageSize: 20 };
      });

      if (typeof payload.unreadCount === 'number') {
        setUnreadCount(payload.unreadCount);
      } else {
        setUnreadCount((c) => c + 1);
      }

      toastRef.current.info(n.title || 'New notification');
    });

    connection.onreconnected(() => {
      setRealtimeConnected(true);
      // Catch up on anything missed while disconnected.
      fetchAll();
    });
    connection.onreconnecting(() => setRealtimeConnected(false));
    connection.onclose(() => setRealtimeConnected(false));

    connection
      .start()
      .then(() => {
        if (!disposed) setRealtimeConnected(true);
      })
      .catch((err) => {
        // Non-fatal: the safety poll still delivers notifications.
        console.warn('[Notifications] Realtime connection failed; falling back to polling.', err);
      });

    return () => {
      disposed = true;
      clearInterval(interval);
      setRealtimeConnected(false);
      connection.stop().catch(() => {});
    };
  }, [isAuthenticated, user?.id, fetchAll]);

  const markAsRead = useCallback(async (id: string) => {
    const previous = dataRef.current;
    const previousUnread = unreadRef.current;
    if (!previous?.items.some((n) => n.id === id && !n.isRead)) return;

    // Optimistic: the dot clears immediately, we undo it only if the API rejects.
    setData((prev) =>
      prev
        ? {
            ...prev,
            items: prev.items.map((n) =>
              n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n
            ),
          }
        : prev
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));

    try {
      await notificationService.markAsRead(id);
    } catch {
      setData(previous);
      setUnreadCount(previousUnread);
      toastRef.current.error('Could not mark notification as read');
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    const previous = dataRef.current;
    const previousUnread = unreadRef.current;

    setData((prev) =>
      prev
        ? {
            ...prev,
            items: prev.items.map((n) => ({
              ...n,
              isRead: true,
              readAt: n.readAt ?? new Date().toISOString(),
            })),
          }
        : prev
    );
    setUnreadCount(0);

    try {
      await notificationService.markAllAsRead();
    } catch {
      setData(previous);
      setUnreadCount(previousUnread);
      toastRef.current.error('Could not mark notifications as read');
    }
  }, []);

  const value: NotificationContextValue = {
    notifications: data?.items ?? [],
    totalCount: data?.totalCount ?? 0,
    unreadCount,
    loading,
    loadingMore,
    hasMore: (data?.items.length ?? 0) < (data?.totalCount ?? 0),
    error,
    realtimeConnected,
    refresh: fetchAll,
    loadMore,
    markAsRead,
    markAllAsRead,
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications(): NotificationContextValue {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
